// src/features/dashboard/components/DashboardHeader.tsx

import {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import {
  DashboardHeaderProps,
  DateOption,
} from '../types/dashboard.types';

import {
  DATE_OPTIONS,
} from '../constants/dashboard.constants';

import {
  getDateRangeText,
} from '../utils/date.utils';

import {
  useDashboardDate,
  DashboardDateContext,
} from '../context/DashboardDateContext';

import {
  useManagerDate,
  ManagerDateContext,
} from '../context/ManagerDateContext';

import {
  useContext,
} from 'react';

/* ============================================================================
   EXTENDED PROPS
============================================================================ */

type ExtendedDashboardHeaderProps =
  DashboardHeaderProps & {
    /**
     * Which date context should be used.
     *
     * dashboard = DashboardDateContext
     * manager   = ManagerDateContext
     */
    dateContext?:
      | 'dashboard'
      | 'manager';
  };

/* ============================================================================
   DASHBOARD HEADER
============================================================================ */

const DashboardHeader = ({
  showBackButton = false,
  breadcrumbs,
  dateContext = 'dashboard',
}: ExtendedDashboardHeaderProps) => {

  /* =========================================================
     NAVIGATION
  ========================================================= */

  const navigate =
    useNavigate();

  /* =========================================================
     DROPDOWN REF
  ========================================================= */

  const dropdownRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  /* =========================================================
     DATE CONTEXTS
     
     IMPORTANT:
     
     Both contexts are read safely.
     
     We do NOT conditionally call hooks because React hooks
     must always be called in the same order.
  ========================================================= */

  const dashboardDate =
    useContext(
      DashboardDateContext,
    );

  const managerDate =
    useContext(
      ManagerDateContext,
    );

  /* =========================================================
     SELECT ACTIVE DATE CONTEXT
  ========================================================= */

  const activeDateContext =
    dateContext === 'manager'
      ? managerDate
      : dashboardDate;

  /* =========================================================
     VALIDATE CONTEXT
  ========================================================= */

  if (!activeDateContext) {
    throw new Error(
      dateContext === 'manager'
        ? 'DashboardHeader with dateContext="manager" must be rendered inside ManagerDateProvider'
        : 'DashboardHeader must be rendered inside DashboardDateProvider',
    );
  }

  /* =========================================================
     DATE VALUES
  ========================================================= */

  const {
    fromDate: startDate,
    toDate: endDate,
    setFromDate: setStartDate,
    setToDate: setEndDate,
  } = activeDateContext;

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showDropdown,
    setShowDropdown,
  ] = useState(false);

  const [
    showCustomCalendar,
    setShowCustomCalendar,
  ] = useState(false);

  /* =========================================================
     BROWSER ZOOM SCALE
  ========================================================= */

  const [
    baseDevicePixelRatio,
    setBaseDevicePixelRatio,
  ] = useState<number | null>(
    null,
  );

  const [
    headerScale,
    setHeaderScale,
  ] = useState(1);

  /* =========================================================
     STORE ORIGINAL DEVICE PIXEL RATIO
  ========================================================= */

  useEffect(() => {
    const ratio =
      window.devicePixelRatio || 1;

    setBaseDevicePixelRatio(
      ratio,
    );
  }, []);

  /* =========================================================
     CALCULATE BROWSER ZOOM
  ========================================================= */

  useEffect(() => {

    if (
      baseDevicePixelRatio === null
    ) {
      return;
    }

    const updateHeaderScale = () => {

      const currentRatio =
        window.devicePixelRatio || 1;

      const browserZoom =
        currentRatio /
        baseDevicePixelRatio;

      let scale = 1;

      /* =====================================================
         100%
      ===================================================== */

      if (
        browserZoom <= 1.05
      ) {
        scale = 1;
      }

      /* =====================================================
         110%
      ===================================================== */

      else if (
        browserZoom <= 1.15
      ) {
        scale = 0.95;
      }

      /* =====================================================
         125%
      ===================================================== */

      else if (
        browserZoom <= 1.35
      ) {
        scale = 0.92;
      }

      /* =====================================================
         150% AND ABOVE
      ===================================================== */

      else {
        scale = 0.90;
      }

      setHeaderScale(
        scale,
      );
    };

    updateHeaderScale();

    window.addEventListener(
      'resize',
      updateHeaderScale,
    );

    const visualViewport =
      window.visualViewport;

    if (visualViewport) {
      visualViewport.addEventListener(
        'resize',
        updateHeaderScale,
      );
    }

    return () => {

      window.removeEventListener(
        'resize',
        updateHeaderScale,
      );

      if (visualViewport) {
        visualViewport.removeEventListener(
          'resize',
          updateHeaderScale,
        );
      }
    };

  }, [
    baseDevicePixelRatio,
  ]);

  /* =========================================================
     SCALE WIDTH
  ========================================================= */

  const scaledWidth =
    headerScale < 1
      ? `${100 / headerScale}%`
      : '100%';

  /* =========================================================
     CLOSE DROPDOWN OUTSIDE
  ========================================================= */

  useEffect(() => {

    const handleClickOutside = (
      event: MouseEvent,
    ) => {

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(
          event.target as Node,
        )
      ) {
        setShowDropdown(
          false,
        );

        setShowCustomCalendar(
          false,
        );
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside,
    );

    return () => {

      document.removeEventListener(
        'mousedown',
        handleClickOutside,
      );
    };

  }, []);

  /* =========================================================
     HANDLE DATE OPTION
  ========================================================= */

  const handleDateOption = (
    option: DateOption,
  ) => {

    const currentDate =
      new Date();

    let from =
      new Date(
        currentDate,
      );

    let to =
      new Date(
        currentDate,
      );

    /* =====================================================
       DATE OPTIONS
    ===================================================== */

    switch (option) {

      /* -----------------------------------------------------
         TODAY
      ----------------------------------------------------- */

      case 'Today':

        from =
          new Date(
            currentDate,
          );

        to =
          new Date(
            currentDate,
          );

        break;

      /* -----------------------------------------------------
         YESTERDAY
      ----------------------------------------------------- */

      case 'Yesterday':

        from =
          new Date(
            currentDate,
          );

        from.setDate(
          currentDate.getDate() - 1,
        );

        to =
          new Date(
            currentDate,
          );

        to.setDate(
          currentDate.getDate() - 1,
        );

        break;

      /* -----------------------------------------------------
         LAST 7 DAYS
      ----------------------------------------------------- */

      case 'Last 7 Days':

        from =
          new Date(
            currentDate,
          );

        from.setDate(
          currentDate.getDate() - 6,
        );

        to =
          new Date(
            currentDate,
          );

        break;

      /* -----------------------------------------------------
         LAST 30 DAYS
      ----------------------------------------------------- */

      case 'Last 30 Days':

        from =
          new Date(
            currentDate,
          );

        from.setDate(
          currentDate.getDate() - 29,
        );

        to =
          new Date(
            currentDate,
          );

        break;

      /* -----------------------------------------------------
         THIS MONTH
      ----------------------------------------------------- */

      case 'This Month':

        from =
          new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            1,
          );

        to =
          new Date(
            currentDate,
          );

        break;

      /* -----------------------------------------------------
         LAST MONTH
      ----------------------------------------------------- */

      case 'Last Month':

        from =
          new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - 1,
            1,
          );

        to =
          new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            0,
          );

        break;

      /* -----------------------------------------------------
         CUSTOM RANGE
      ----------------------------------------------------- */

      case 'Custom Range':

        setShowCustomCalendar(
          true,
        );

        return;

      /* -----------------------------------------------------
         DEFAULT
      ----------------------------------------------------- */

      default:

        break;
    }

    /* =====================================================
       UPDATE ACTIVE DATE CONTEXT
       
       This is now the critical fix.
       
       Manager dashboard:
          ManagerDateContext gets updated.
       
       Normal dashboard:
          DashboardDateContext gets updated.
    ===================================================== */

    setStartDate(
      from,
    );

    setEndDate(
      to,
    );

    /* =====================================================
       CLOSE DROPDOWN
    ===================================================== */

    setShowDropdown(
      false,
    );

    setShowCustomCalendar(
      false,
    );
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (

    <div
      className="
        w-full
        min-w-0
        overflow-visible
      "
    >

      {/* =====================================================
          SCALE WRAPPER
      ===================================================== */}

      <div
        className="
          origin-top-left
          min-w-0
          max-w-none
        "
        style={{
          width:
            scaledWidth,

          zoom:
            headerScale,

          boxSizing:
            'border-box',
        }}
      >

        {/* ===================================================
            HEADER ROW
        =================================================== */}

        <div
          className="
            mb-5
            flex
            w-full
            min-w-0
            flex-row
            items-center
            justify-between
            gap-2
            overflow-visible
          "
        >

          {/* =================================================
              LEFT - BREADCRUMBS
          ================================================= */}

          <div
            className="
              flex
              min-w-0
              flex-1
              shrink
              items-center
              gap-2
              overflow-hidden
              whitespace-nowrap
              text-[13px]
            "
          >

            {breadcrumbs.map(
              (
                item,
                index,
              ) => (

                <div
                  key={index}
                  className="
                    flex
                    shrink-0
                    items-center
                    gap-2
                    whitespace-nowrap
                  "
                >

                  <span
                    onClick={() =>
                      item.path &&
                      navigate(
                        item.path,
                      )
                    }
                    className={`
                      transition-colors

                      ${
                        item.path
                          ? `
                            cursor-pointer
                            hover:text-[#002B5B]
                          `
                          : ''
                      }

                      ${
                        index ===
                        breadcrumbs.length - 1
                          ? `
                            font-semibold
                            text-[#002B5B]
                          `
                          : `
                            text-[#667085]
                          `
                      }
                    `}
                  >
                    {item.label}
                  </span>

                  {index !==
                    breadcrumbs.length - 1 && (

                    <span
                      className="
                        shrink-0
                        text-[#98A2B3]
                      "
                    >
                      /
                    </span>

                  )}

                </div>

              ),
            )}

          </div>

          {/* =================================================
              RIGHT SIDE
          ================================================= */}

          <div
            className="
              flex
              shrink-0
              flex-row
              items-center
              gap-2
              whitespace-nowrap
            "
          >

            {/* ===============================================
                DATE PICKER
            =============================================== */}

            <div
              ref={dropdownRef}
              className="
                relative
                shrink-0
              "
            >

              <input
                type="text"
                readOnly
                value={
                  getDateRangeText(
                    startDate,
                    endDate,
                  )
                }
                onClick={() =>
                  setShowDropdown(
                    !showDropdown,
                  )
                }
                className="
                  h-[34px]
                  w-[230px]
                  cursor-pointer
                  rounded-[6px]
                  border
                  border-[#D0D5DD]
                  bg-white
                  px-3
                  text-[12px]
                  text-[#344054]
                  outline-none
                "
              />

              {/* =============================================
                  DATE DROPDOWN
              ============================================= */}

              {showDropdown && (

                <div
                  className="
                    absolute
                    right-0
                    top-[40px]
                    z-50
                    flex
                    rounded-[8px]
                    border
                    border-[#D0D5DD]
                    bg-white
                    shadow-xl
                  "
                >

                  {/* =========================================
                      DATE OPTIONS
                  ========================================= */}

                  <div
                    className="
                      min-w-[170px]
                      border-r
                      border-[#EAECF0]
                    "
                  >

                    {DATE_OPTIONS.map(
                      (item) => (

                        <div
                          key={item}
                          onClick={() =>
                            handleDateOption(
                              item as DateOption,
                            )
                          }
                          className="
                            cursor-pointer
                            whitespace-nowrap
                            px-4
                            py-3
                            text-[12px]
                            text-[#101828]
                            transition-colors
                            hover:bg-[#F9FAFB]
                          "
                        >
                          {item}
                        </div>

                      ),
                    )}

                  </div>

                  {/* =========================================
                      CUSTOM CALENDAR
                  ========================================= */}

                  {showCustomCalendar && (

                    <div
                      className="
                        flex
                        gap-3
                        bg-white
                        p-3
                      "
                    >

                      {/* =====================================
                          FROM DATE
                      ===================================== */}

                      <div>

                        <p
                          className="
                            mb-2
                            text-[12px]
                            font-semibold
                            text-[#344054]
                          "
                        >
                          From Date
                        </p>

                        <DatePicker
                          selected={
                            startDate
                          }
                          onChange={(
                            date: Date | null,
                          ) => {

                            if (date) {
                              setStartDate(
                                date,
                              );
                            }

                          }}
                          inline
                        />

                      </div>

                      {/* =====================================
                          TO DATE
                      ===================================== */}

                      <div>

                        <p
                          className="
                            mb-2
                            text-[12px]
                            font-semibold
                            text-[#344054]
                          "
                        >
                          To Date
                        </p>

                        <DatePicker
                          selected={
                            endDate
                          }
                          onChange={(
                            date: Date | null,
                          ) => {

                            if (date) {
                              setEndDate(
                                date,
                              );
                            }

                          }}
                          inline
                          minDate={
                            startDate
                          }
                        />

                      </div>

                    </div>

                  )}

                </div>

              )}

            </div>

            {/* =================================================
                SEARCH
            ================================================= */}

            <button
              type="button"
              onClick={() => {

                /*
                 * Date state has already been updated when
                 * the user selects an option.
                 *
                 * This button intentionally closes the
                 * dropdown and keeps the selected range.
                 */

                setShowDropdown(
                  false,
                );

                setShowCustomCalendar(
                  false,
                );
              }}
              className="
                h-[34px]
                shrink-0
                rounded-[6px]
                bg-[#03CAA1]
                px-4
                text-[12px]
                font-semibold
                text-[#002B5B]
              "
            >
              Search
            </button>

            {/* =================================================
                BACK
            ================================================= */}

            {showBackButton && (

              <button
                type="button"
                onClick={() =>
                  navigate(-1)
                }
                className="
                  h-[38px]
                  shrink-0
                  rounded-[8px]
                  border
                  border-[#D0D5DD]
                  bg-white
                  px-4
                  text-[12px]
                  font-medium
                  text-[#475467]
                "
              >
                Back
              </button>

            )}

          </div>

        </div>

      </div>

    </div>
  );
};

export default DashboardHeader;